import { z } from 'zod';
import { ActionItemApi, ActionItemSchema } from '../action';
import { Prettify } from '../utility';
import { ThemeRichTextNodeArray } from './rich-text';

export const SectionActionType = {
  Email: 'email',
  File: 'file',
  Flow: 'flow',
  Giving: 'giving',
  InfoCard: 'infoCard',
  Leader: 'leader',
  None: 'none',
  Overlay: 'overlay',
  Page: 'page',
  Phone: 'phone',
  Posts: 'posts',
  Prayer: 'prayer',
  Sermons: 'sermons',
  SignIn: 'signIn',
  Subscribe: 'subscribe',
  Url: 'url',
} as const;

export type SectionActionType = (typeof SectionActionType)[keyof typeof SectionActionType];

export const ActionTypeLabelMap: Record<SectionActionType, string> = {
  [SectionActionType.Email]: 'Email',
  [SectionActionType.File]: 'File Download',
  [SectionActionType.Flow]: 'Flow',
  [SectionActionType.Giving]: 'Giving',
  [SectionActionType.InfoCard]: 'Info Card',
  [SectionActionType.Leader]: 'Leader',
  [SectionActionType.None]: 'None',
  [SectionActionType.Overlay]: 'Overlay',
  [SectionActionType.Page]: 'Page',
  [SectionActionType.Phone]: 'Phone',
  [SectionActionType.Posts]: 'Posts',
  [SectionActionType.Prayer]: 'Prayer',
  [SectionActionType.Sermons]: 'Sermons',
  [SectionActionType.SignIn]: 'Sign In',
  [SectionActionType.Subscribe]: 'Subscribe',
  [SectionActionType.Url]: 'URL',
};

interface SectionActionBase {
  openInNewTab?: boolean;
  displayAs: 'style1' | 'style2' | 'style3' | 'style4';
}

export type OverlayAction = z.infer<typeof OverlayActionSchema>;

export type SectionAction = z.infer<typeof SectionActionSchema>;

/**
 * Narrows {@link SectionAction} by `type`. Prefer this over `Extract<SectionAction, { type: T }>`: most
 * variants share one Zod branch with a wide `type` enum, so `Extract` by a single literal becomes `never`.
 */
export type SectionActionOfType<T extends SectionActionType> = SectionAction & { type: T };

export const OverlayActionSchema = ActionItemSchema.extend({
  type: z.literal(SectionActionType.Overlay),
  overlayId: z.string(),
});

export const SubscribeActionSchema = ActionItemSchema.extend({
  type: z.literal(SectionActionType.Subscribe),
  firstNamePlaceholder: z.string().optional(),
  lastNamePlaceholder: z.string().optional(),
  emailPlaceholder: z.string().optional(),
  submitButtonText: z.string().optional(),
  successMessage: z.string().optional(),
  errorMessage: z.string().optional(),
  disclaimerText: z.union([z.string(), z.custom<ThemeRichTextNodeArray>((value) => Array.isArray(value))]).optional(),
});

const DisplayAsSchema = z.enum(['style1', 'style2', 'style3', 'style4']).optional();

export const SectionActionSchema = z.discriminatedUnion('type', [
  ActionItemSchema.extend({
    type: z.enum(
      Object.values(SectionActionType).filter(
        (type) => type !== SectionActionType.Overlay && type !== SectionActionType.Subscribe
      )
    ),
    displayAs: DisplayAsSchema,
  }),
  OverlayActionSchema.extend({
    displayAs: DisplayAsSchema,
  }),
  SubscribeActionSchema.extend({
    displayAs: DisplayAsSchema,
  }),
]);

type ActionApi<T, M = {}> = Prettify<ActionItemApi<T, M> & SectionActionBase>;
type DistributeActionApi<T> = T extends any ? ActionApi<T> : never;

type FileActionApi = ActionItemApi<'file', { readUrl?: string }>;

export type SectionActionApi = DistributeActionApi<Exclude<SectionActionType, 'file'>> | FileActionApi;
